import { useMemo } from 'react'
import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { getAllBillingPeriodsOptions, getAllVehiclesOptions, getRequirementSubintervalsOptions } from '@/api/main'
import type { AwareDatetimeInterval, DriveSubinterval, ScheduledDriveResponseWithEndpoints } from '@/api/main/types.gen'
import { getOverlappingBillingPeriods } from '@/utils/scheduling'
import { useVehicleColors } from './colors'

export type DriveMapDrive = ScheduledDriveResponseWithEndpoints & {
  // Always present here — we filter out drives that lack either endpoint.
  origin_point: NonNullable<ScheduledDriveResponseWithEndpoints['origin_point']>
  destination_point: NonNullable<ScheduledDriveResponseWithEndpoints['destination_point']>
  subInterval: AwareDatetimeInterval
}

const sameInterval = (a: AwareDatetimeInterval, b: AwareDatetimeInterval) =>
  a.starting_from === b.starting_from && a.ending_at === b.ending_at

export const useDriveMapData = ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
  const startIso = dayjs(startDate).startOf('day').toISOString()
  const endIso = dayjs(endDate).endOf('day').toISOString()

  const { data: billingPeriods } = useSuspenseQuery(getAllBillingPeriodsOptions())
  const { data: vehicles } = useSuspenseQuery(getAllVehiclesOptions())

  const overlappingPeriods = useMemo(
    () => getOverlappingBillingPeriods(startIso, endIso, billingPeriods),
    [billingPeriods, startIso, endIso]
  )

  const subintervalQueries = useMemo(
    () =>
      overlappingPeriods.map(period =>
        getRequirementSubintervalsOptions({
          path: {
            billing_period_id: period.id,
            starting_from: startIso,
            ending_at: endIso,
          },
        })
      ),
    [overlappingPeriods, startIso, endIso]
  )

  const driveSubintervals = useSuspenseQueries({
    queries: subintervalQueries,
    combine: results => results.flatMap(r => r.data.drive_subintervals as DriveSubinterval[]),
  })

  const allDrives = useMemo<DriveMapDrive[]>(
    () =>
      driveSubintervals.flatMap(subInt =>
        subInt.drives
          .filter(d => d.origin_point && d.destination_point)
          .map(d => ({
            ...d,
            origin_point: d.origin_point!,
            destination_point: d.destination_point!,
            subInterval: subInt.interval,
          }))
      ),
    [driveSubintervals]
  )

  const vehicleIds = useMemo(() => {
    const fromDrives = new Set(allDrives.map(d => d.vehicle_id))
    const fromCatalog = new Set(vehicles.map(v => v.id))
    return Array.from(new Set([...fromCatalog, ...fromDrives])).sort()
  }, [allDrives, vehicles])

  const selectableIntervals = useMemo(() => {
    const out: AwareDatetimeInterval[] = []
    for (const sub of driveSubintervals) {
      if (!out.some(i => sameInterval(i, sub.interval))) out.push(sub.interval)
    }
    return out.sort((a, b) => a.starting_from.localeCompare(b.starting_from))
  }, [driveSubintervals])

  const vehicleColors = useVehicleColors(vehicleIds)

  return { allDrives, vehicleIds, selectableIntervals, vehicleColors }
}

export { sameInterval }
